/**
 * ── useHallRecommendation ──────────────────────────────────────────────────────
 *
 * React hook that wires the availability API response into the hall
 * recommendation engine and exposes pricing-aware recommendations.
 */

import { useMemo } from "react";
import {
  getHallRecommendations,
  getBestHallCombination,
  getCapacityOnlyRecommendation,
  HallPricing,
  HallRecommendation,
} from "./hallRecommendation";

// ── Types for the raw API pricing response ───────────────────────────────────

interface RawPricingRecord {
  hallKey: string;
  timeSlot: string;
  flatFee: number | string;
  pricePerPerson: number | string;
}

/** Shape returned by the availability API: hallKey → RawPricingRecord[] */
type RawPricingMap = Record<string, RawPricingRecord[]>;

// ── Helper: extract the right pricing per hallKey for a given timeSlot ───────

function buildPricingMap(
  rawPricing: RawPricingMap | undefined,
  timeSlot: string,
): Record<string, HallPricing> {
  const map: Record<string, HallPricing> = {};
  if (!rawPricing) return map;

  for (const [hallKey, records] of Object.entries(rawPricing)) {
    if (!Array.isArray(records)) continue;

    // Find exact timeSlot match first, then FULL_DAY as fallback
    const match =
      records.find((r) => r.timeSlot === timeSlot) ||
      records.find((r) => r.timeSlot === "FULL_DAY");

    if (match) {
      map[hallKey] = {
        flatFee: Number(match.flatFee) || 0,
        pricePerPerson: Number(match.pricePerPerson) || 0,
      };
    }
  }

  return map;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseHallRecommendationParams {
  guestCount: number;
  timeSlot: string;
  occupied: Record<string, Record<string, any>>;
  rawPricing: RawPricingMap | undefined;
}

interface UseHallRecommendationResult {
  /** All valid recommendations sorted by price, tagged with best_value / cheapest */
  recommendations: HallRecommendation[];
  /** The single best auto-select option (best_value mode) */
  bestPick: HallRecommendation | null;
  /** The cheapest option */
  cheapestPick: HallRecommendation | null;
  /** Savings between best_value and cheapest (0 if same) */
  savings: number;
  /** Whether pricing data is available */
  hasPricing: boolean;
  /** Pricing map for direct lookup */
  pricingMap: Record<string, HallPricing>;
  /** Capacity-only fallback when no pricing available */
  capacityFallback: ReturnType<typeof getCapacityOnlyRecommendation>;
}

export function useHallRecommendation({
  guestCount,
  timeSlot,
  occupied,
  rawPricing,
}: UseHallRecommendationParams): UseHallRecommendationResult {
  const pricingMap = useMemo(
    () => buildPricingMap(rawPricing, timeSlot),
    [rawPricing, timeSlot],
  );

  const hasPricing = Object.keys(pricingMap).length > 0;

  const recommendations = useMemo(
    () =>
      hasPricing
        ? getHallRecommendations(guestCount, timeSlot, occupied, pricingMap)
        : [],
    [guestCount, timeSlot, occupied, pricingMap, hasPricing],
  );

  const bestPick = useMemo(
    () =>
      hasPricing
        ? getBestHallCombination(guestCount, timeSlot, occupied, pricingMap, "best_value")
        : null,
    [guestCount, timeSlot, occupied, pricingMap, hasPricing],
  );

  const cheapestPick = useMemo(
    () =>
      hasPricing
        ? getBestHallCombination(guestCount, timeSlot, occupied, pricingMap, "cheapest")
        : null,
    [guestCount, timeSlot, occupied, pricingMap, hasPricing],
  );

  const savings = useMemo(() => {
    if (!bestPick || !cheapestPick) return 0;
    if (bestPick.hallKey === cheapestPick.hallKey) return 0;
    return Math.round((bestPick.totalPrice - cheapestPick.totalPrice) * 100) / 100;
  }, [bestPick, cheapestPick]);

  const capacityFallback = useMemo(
    () => getCapacityOnlyRecommendation(guestCount, timeSlot, occupied),
    [guestCount, timeSlot, occupied],
  );

  return {
    recommendations,
    bestPick,
    cheapestPick,
    savings,
    hasPricing,
    pricingMap,
    capacityFallback,
  };
}
